import { Button, MenuItem, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  AUTOMATED_CONNECTION_ADDED_TEXT,
  AUTOMATED_CONNECTION_API_KEY_HELPER,
  AUTOMATED_CONNECTION_CREATE_INITIAL_VALUES,
  AUTOMATED_CONNECTION_CREATE_TITLE,
  AUTOMATED_CONNECTION_CREATE_VALIDATION_SCHEMA,
  AUTOMATED_CONNECTION_FIELD_LABELS,
  AUTOMATED_CONNECTION_SAVE_ERROR_TEXT,
  CONNECTION_TYPES_SELECT_OPTIONS,
  NAME,
  TOKEN,
  TYPE,
} from '../../../constants/automations';
import useFocusFirstInvalidField from '../../../hooks/useFocusFirstInvalidField';
import useRegisterCloseRequest from '../../../hooks/useRegisterCloseRequest';
import { addAutomatedConnectionRequest } from '../../../store/actions/automations';
import { IAddAutomatedConnectionActionPayload, ICreateAutomatedConnectionFormValues } from '../../../types/automations';
import { IApiError, NotificationListEnum } from '../../../types/shell';
import notification from '../../../utils/notification';
import { handleApiError } from '../../../utils/ui';
import { SidebarFormLayout } from '../../_mui/_components_/SidebarFormLayout';
import { SecretTextField } from '../../_mui/_controls_/SecretTextField';

interface IProps {
  orgId: string;
  onClose?: () => void;
  closeRequestRef?: React.MutableRefObject<(() => void) | null>;
}

type TFieldName = keyof ICreateAutomatedConnectionFormValues;

const TYPE_SELECT_DISPLAY_PROPS = {
  'data-action': 'automated_connection_type_select',
} as React.HTMLAttributes<HTMLDivElement>;

export const buildCreateAutomatedConnectionPayload = (
  values: ICreateAutomatedConnectionFormValues,
): IAddAutomatedConnectionActionPayload['automatedConnection'] =>
  ({
    ...values,
    [NAME]: values[NAME].trim(),
    [TOKEN]: values[TOKEN].trim(),
  } as IAddAutomatedConnectionActionPayload['automatedConnection']);

const preventImplicitSubmit = (e: React.FormEvent<HTMLFormElement>) => e.preventDefault();

const CreateAutomatedConnectionForm: React.FC<IProps> = ({ orgId, onClose, closeRequestRef }) => {
  const dispatch = useDispatch();

  const handleSubmit = useCallback(
    (values: ICreateAutomatedConnectionFormValues) =>
      new Promise((resolve, reject) => {
        dispatch(
          addAutomatedConnectionRequest({
            automatedConnection: buildCreateAutomatedConnectionPayload(values),
            orgId,
            resolve,
            reject,
          }),
        );
      })
        .then(() => {
          notification.success(NotificationListEnum.Success, { content: AUTOMATED_CONNECTION_ADDED_TEXT });
          onClose?.();
        })
        .catch((error: IApiError) => handleApiError(AUTOMATED_CONNECTION_SAVE_ERROR_TEXT)(error)),
    [dispatch, orgId, onClose],
  );

  const form = useFormik<ICreateAutomatedConnectionFormValues>({
    initialValues: AUTOMATED_CONNECTION_CREATE_INITIAL_VALUES,
    validationSchema: AUTOMATED_CONNECTION_CREATE_VALIDATION_SCHEMA,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  const { errors, touched, getFieldProps, submitForm, isSubmitting, submitCount } = form;

  const formRef = useRef<HTMLFormElement>(null);

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  useFocusFirstInvalidField(formRef, { submitCount, isSubmitting, hasErrors });

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose?.();
    }
  }, [isSubmitting, onClose]);

  useRegisterCloseRequest(closeRequestRef, handleClose);

  const getError = useCallback(
    (fieldName: TFieldName) => (touched[fieldName] && errors[fieldName] ? String(errors[fieldName]) : undefined),
    [errors, touched],
  );

  const nameError = getError(NAME);
  const typeError = getError(TYPE);
  const tokenError = getError(TOKEN);

  return (
    <SidebarFormLayout
      title={AUTOMATED_CONNECTION_CREATE_TITLE}
      onClose={handleClose}
      isCloseDisabled={isSubmitting}
      isLoading={isSubmitting}
      actions={
        <>
          <Button
            variant="text"
            size="large"
            onClick={handleClose}
            disabled={isSubmitting}
            data-action="automated_connection_cancel_btn"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isSubmitting}
            onClick={submitForm}
            data-action="automated_connection_save_btn"
          >
            Save
          </Button>
        </>
      }
    >
      <form ref={formRef} noValidate onSubmit={preventImplicitSubmit}>
        <Stack spacing={3} useFlexGap sx={{ pt: 1 }}>
          <TextField
            {...getFieldProps(NAME)}
            label={AUTOMATED_CONNECTION_FIELD_LABELS[NAME]}
            required
            fullWidth
            autoComplete="off"
            error={Boolean(nameError)}
            helperText={nameError}
            slotProps={{ htmlInput: { 'data-action': 'automated_connection_name_input' } }}
          />
          <TextField
            {...getFieldProps(TYPE)}
            select
            label={AUTOMATED_CONNECTION_FIELD_LABELS[TYPE]}
            required
            fullWidth
            error={Boolean(typeError)}
            helperText={typeError}
            slotProps={{ select: { SelectDisplayProps: TYPE_SELECT_DISPLAY_PROPS } }}
          >
            {CONNECTION_TYPES_SELECT_OPTIONS.map(({ value, label, disabled }) => (
              <MenuItem key={value} value={value} disabled={disabled}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <SecretTextField
            {...getFieldProps(TOKEN)}
            label={AUTOMATED_CONNECTION_FIELD_LABELS[TOKEN]}
            required
            error={Boolean(tokenError)}
            helperText={tokenError ?? AUTOMATED_CONNECTION_API_KEY_HELPER}
            dataAction="automated_connection_token_input"
          />
        </Stack>
      </form>
    </SidebarFormLayout>
  );
};

export default CreateAutomatedConnectionForm;
